import { Popover, PopoverButton } from "@headlessui/react"
import { EyeOff } from "lucide-react"
import { ReactNode, useMemo } from "react"

import {
  ButtonProps,
  NonTonalButtonMode,
} from "../../../../../components/base/control/Button"
import { PopoverPanel } from "../../../../../components/base/overlay/Popover"
import {
  popoverPanelAnchor,
  popoverClassName,
  modalPopoverClassName,
} from "../../../../../components/base/overlay/Popover/popoverStyles"
import { PopoverDualSelect } from "../../../../../components/composite/popover"
import { classNames } from "../../../../../lib/classNames"
import { DropdownButton } from "../../DropdownButton/DropdownButton"
import { getColumnId } from "../../useTable/getColumnId"
import { Column, ColumnType } from "../../useTable/types"

export interface HideColumnsButtonProps<T> {
  columns: Column<T>[]
  hiddenColumns: Set<string>
  toggleColumnHideShow: (columnId: string) => void
  clearHiddenColumns: () => void
  hideAllColumns: (columnIds: string[]) => void
  isInModal?: boolean
}

interface ColumnHeaderData {
  id: string
  title: string
  content: ReactNode
}

export function HideColumnsButton<T>({
  hiddenColumns,
  columns,
  toggleColumnHideShow,
  clearHiddenColumns,
  hideAllColumns,
  isInModal,
}: HideColumnsButtonProps<T>) {
  const columnHeaderData: ColumnHeaderData[] = useMemo(() => {
    return columns.flatMap((col) => {
      if (col.type === ColumnType.Group) {
        return col.columns.map((subCol) => ({
          id: getColumnId(subCol, col),
          title: subCol.title,
          content: subCol.renderTitle?.(subCol.title) ?? subCol.title,
        }))
      }
      return [
        {
          id: getColumnId(col),
          title: col.title,
          content: col.renderTitle?.(col.title) ?? col.title,
        },
      ]
    })
  }, [columns])

  function handleHideAll() {
    hideAllColumns(columnHeaderData.map((column) => column.id))
  }

  return (
    <Popover>
      <PopoverButton
        as={DropdownHideButton}
        mode="secondary"
        selected={hiddenColumns.size > 0}
      />
      <PopoverPanel
        anchor={popoverPanelAnchor}
        className={classNames(
          isInModal ? modalPopoverClassName : popoverClassName,
          "w-fit"
        )}
      >
        <PopoverDualSelect
          items={columnHeaderData}
          selectedItemKeys={hiddenColumns}
          keyExtractor={(column: ColumnHeaderData) => column.id}
          valueExtractor={(column: ColumnHeaderData) => column.title}
          renderValue={(column: ColumnHeaderData) => column.content}
          onSelect={(column: ColumnHeaderData) =>
            toggleColumnHideShow(column.id)
          }
          onSelectAll={handleHideAll}
          onDeselectAll={clearHiddenColumns}
          header="Show/hide fields"
          selectedLabel="Hidden fields"
          deselectedLabel="Shown fields"
          selectAllButtonName="Hide all"
          deselectAllButtonName="Show all"
          showIcons
          showSelectedFirst={false}
        />
      </PopoverPanel>
    </Popover>
  )
}

const DropdownHideButton = (
  props: Omit<ButtonProps, "mode" | "tone"> & { mode: NonTonalButtonMode }
) => {
  return <DropdownButton icon={EyeOff} label={"Show/hide"} {...props} />
}
